import type { Environments } from "@paddle/paddle-js";

export type PaddlePriceBreakdown = {
  formattedSubtotal: string | null;
  formattedTax: string | null;
  formattedTotal: string | null;
  subtotal: string | null;
  tax: string | null;
  total: string | null;
  taxRate: string | null;
  countryCode: string | null;
  postalCode: string | null;
  currencyCode: string | null;
  /** Налог внутри цены (`tax_mode: internal`): цена каталога — это `total`, а не `subtotal`. */
  taxInclusive?: boolean;
};

type PaddleLineItem = {
  price?: {
    id?: string;
    taxMode?: string;
    unitPrice?: { currencyCode?: string };
  };
  formattedTotals?: {
    subtotal?: string;
    tax?: string;
    total?: string;
  };
  totals?: { subtotal?: string; tax?: string; total?: string };
  taxRate?: string;
};

type PaddlePreviewResponse = {
  data?: {
    currencyCode?: string;
    details?: {
      address?: {
        countryCode?: string;
        postalCode?: string;
        country_code?: string;
        postal_code?: string;
      };
      lineItems?: PaddleLineItem[];
    };
  };
};

const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG", "RWF",
  "UGX", "VND", "VUV", "XAF", "XOF", "XPF",
]);

/**
 * Язык — не страна.
 *
 * Локаль `/es/` раньше означала Испанию, и витрина показывала евро с испанским
 * IVA любому испаноязычному посетителю — в том числе из Мексики, где и валюта,
 * и ставка налога другие. Биллинговую страну определяет Paddle по IP
 * покупателя, а подтверждает покупатель в чекауте.
 *
 * Оверрайд ниже существует только для QA: иначе проверить испанскую или
 * бразильскую витрину с рабочего места в России невозможно — Paddle всегда
 * отдаёт базовый USD.
 */
function readPaddleCountryOverride() {
  if (typeof window === "undefined") return null;
  try {
    const countryCode = String(new URLSearchParams(window.location.search).get("country") || "")
      .trim()
      .toUpperCase();
    return /^[A-Z]{2}$/.test(countryCode) ? countryCode : null;
  } catch {
    return null;
  }
}

/**
 * Адрес превью — только QA-оверрайд `?country=`, иначе адреса нет вовсе.
 *
 * Без адреса Paddle сам определяет страну по IP покупателя и сам применяет
 * оверрайды цен по странам (например `ES,IT → EUR`). Локаль страницы страну не
 * подменяет ни для одного языка.
 *
 * История: 14.10.2026 итальянской локали прибили адрес IT/00118, чтобы `/it/`
 * всегда показывал евро; 15.10.2026 пин убран в пользу IP — локаль не
 * подменяет биллинговую страну (инвариант `docs/domains/internationalization.md`
 * мета-репо). Кабинет (transcripta-frontend) убирает такой же пин параллельно.
 */
export function buildPaddlePreviewRequest(priceIds: string[]) {
  const countryCode = readPaddleCountryOverride();
  return {
    items: priceIds.map((priceId) => ({ priceId, quantity: 1 })),
    ...(countryCode ? { address: { countryCode } } : {}),
  };
}

/**
 * Валюта витрины приходит от Paddle, а не угадывается по символу.
 *
 * Раньше валюта определялась по началу отформатированной строки, и любой `$`
 * означал USD. Для USD, EUR и BRL это сходилось, но ломается на половине мира,
 * как только у цены появляется валюта за пределами этих трёх: на sandbox
 * 2026-10-06 Paddle отдал MXN как «$180.72», CAD — «$14.27», NZD — «$17.86»,
 * CLP — «$9.734». Мексиканская цена получила бы ярлык USD, а чилийская — ещё и
 * деление на 100 в месячном эквиваленте годовой цены, потому что CLP считался
 * бы двухзначной валютой.
 *
 * Источник истины — `data.currencyCode` превью: проверен и в REST API, и в
 * Paddle.js. Валюта каталога (`price.unitPrice.currencyCode`) при любой
 * локализации остаётся USD, поэтому спрашивать её нельзя.
 *
 * Разбор символа остался последним фоллбэком и сознательно молчит на одиноком
 * `$`: его делят USD, MXN, CAD, NZD, ARS, CLP и COP.
 *
 * Рубля в списке нет намеренно: страж артефакта Scriptario запрещает знак рубля
 * в бандле, а Paddle и так не принимает российский адрес, поэтому превью в
 * рублях для покупателя Scriptario невозможно.
 */
const UNAMBIGUOUS_CURRENCY_MARKERS: ReadonlyArray<readonly [string, string]> = [
  ["US$", "USD"],
  ["HK$", "HKD"],
  ["A$", "AUD"],
  ["S$", "SGD"],
  ["R$", "BRL"],
  ["€", "EUR"],
  ["£", "GBP"],
  ["₹", "INR"],
  ["₩", "KRW"],
  ["₺", "TRY"],
  ["฿", "THB"],
  ["zł", "PLN"],
  ["CHF", "CHF"],
  ["EUR", "EUR"],
  ["USD", "USD"],
];

function inferCurrencyCodeFromFormattedAmount(value: string | undefined) {
  const normalized = String(value || "").trim();
  if (!normalized) return null;
  const marker = UNAMBIGUOUS_CURRENCY_MARKERS.find(([symbol]) => normalized.includes(symbol));
  return marker ? marker[1] : null;
}

export const paddlePriceIds = {
  basic: {
    month: process.env.NEXT_PUBLIC_PADDLE_PRICE_ESSENTIAL_MONTH || "",
    year: process.env.NEXT_PUBLIC_PADDLE_PRICE_ESSENTIAL_YEAR || "",
  },
  standard: {
    month: process.env.NEXT_PUBLIC_PADDLE_PRICE_PROFESSIONAL_MONTH || "",
    year: process.env.NEXT_PUBLIC_PADDLE_PRICE_PROFESSIONAL_YEAR || "",
  },
  expert: {
    month: process.env.NEXT_PUBLIC_PADDLE_PRICE_PREMIUM_MONTH || "",
    year: process.env.NEXT_PUBLIC_PADDLE_PRICE_PREMIUM_YEAR || "",
  },
} as const;

/**
 * Цена дополнительной минуты — одна на тариф, без вариантов по локали.
 *
 * Запрашивается тем же превью, что и тарифы, чтобы ставка «сверх лимита»
 * приходила от Paddle в валюте страны покупателя. Переменные необязательные: без
 * них карточка падает на локальную таблицу ставок и для незнакомой валюты просто
 * не показывает строку.
 */
export const paddleExtraMinutePriceIds: Record<string, string> = {
  basic: process.env.NEXT_PUBLIC_PADDLE_PRICE_EXTRA_MINUTES_ESSENTIAL || "",
  standard: process.env.NEXT_PUBLIC_PADDLE_PRICE_EXTRA_MINUTES_PROFESSIONAL || "",
  expert: process.env.NEXT_PUBLIC_PADDLE_PRICE_EXTRA_MINUTES_PREMIUM || "",
};

export function getExtraMinuteBreakdown(
  prices: Record<string, PaddlePriceBreakdown>,
  planId: string,
) {
  const priceId = paddleExtraMinutePriceIds[planId];
  return priceId ? prices[priceId] || null : null;
}

export function getPaddleLandingConfig() {
  const token = process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN || "";
  const environment = process.env.NEXT_PUBLIC_PADDLE_ENV;
  const priceIds = Object.values(paddlePriceIds).flatMap((periods) => Object.values(periods));

  if (!token || !priceIds.every(Boolean)) return null;
  if (environment !== "sandbox" && environment !== "production") return null;

  // Цены минут необязательны, поэтому отсутствующие просто не попадают в запрос.
  const extraMinutePriceIds = Object.values(paddleExtraMinutePriceIds).filter(Boolean);

  return {
    token,
    environment: environment as Environments,
    priceIds: [...priceIds, ...extraMinutePriceIds],
  };
}

export function parsePaddlePricePreview(response: PaddlePreviewResponse) {
  const lineItems = response.data?.details?.lineItems || [];
  const address = response.data?.details?.address;
  const countryCode = address?.countryCode || address?.country_code || null;
  const postalCode = address?.postalCode || address?.postal_code || null;
  // Валюта превью одна на весь ответ, и она же — валюта всех производных сумм.
  const previewCurrencyCode = response.data?.currencyCode || null;
  return lineItems.reduce<Record<string, PaddlePriceBreakdown>>((prices, item) => {
    const priceId = item.price?.id;
    const formatted = item.formattedTotals;
    if (!priceId || !formatted?.subtotal || !formatted.total) return prices;

    const currencyCode = previewCurrencyCode
      || inferCurrencyCodeFromFormattedAmount(formatted.subtotal || formatted.total)
      || null;

    prices[priceId] = {
      formattedSubtotal: formatted.subtotal,
      formattedTax: formatted.tax || null,
      formattedTotal: formatted.total,
      subtotal: item.totals?.subtotal ?? null,
      tax: item.totals?.tax ?? null,
      total: item.totals?.total ?? null,
      taxRate: item.taxRate ?? null,
      countryCode,
      postalCode,
      currencyCode,
      taxInclusive: item.price?.taxMode === "internal",
    };
    return prices;
  }, {});
}

export function getPlanBreakdown(
  prices: Record<string, PaddlePriceBreakdown>,
  planId: string,
  period: "month" | "year",
) {
  if (!(planId in paddlePriceIds)) return null;
  const priceId = paddlePriceIds[planId as keyof typeof paddlePriceIds][period];
  return priceId ? prices[priceId] || null : null;
}

/**
 * Цена, которую видит покупатель, — это цена каталога, а не «подытог» превью.
 *
 * Пока налог начислялся сверху (`tax_mode: external`), цена каталога совпадала
 * с `subtotal`, а Paddle добавлял налог в чекауте. С октября 2026 налог
 * Scriptario платит сам: цены переведены в `tax_mode: internal`, налог сидит
 * внутри цены, и тогда цена каталога — это `total`, а `subtotal` — цена за
 * вычетом налога (испанец увидел бы €6,60 при списании €7,99).
 *
 * Режим берётся из самого превью, а не из сборки: витрина верна и до, и после
 * переключения цен в Paddle. Зеркало — `getPaddleListAmount` во фронтенде.
 */
export function getPaddleListAmount(breakdown: PaddlePriceBreakdown | null | undefined) {
  if (!breakdown) return null;
  return breakdown.taxInclusive ? breakdown.total : breakdown.subtotal;
}

export function getPaddleFormattedListAmount(breakdown: PaddlePriceBreakdown | null | undefined) {
  if (!breakdown) return null;
  return (breakdown.taxInclusive ? breakdown.formattedTotal : breakdown.formattedSubtotal) || null;
}

export function hasPaddleTaxEstimate(breakdown: PaddlePriceBreakdown | null) {
  if (!breakdown?.formattedTax || !breakdown.formattedTotal) return false;

  const taxAmount = breakdown.tax == null ? Number.NaN : Number(breakdown.tax);
  const taxRate = breakdown.taxRate == null ? Number.NaN : Number(breakdown.taxRate);
  if (taxAmount > 0 || taxRate > 0) return true;

  return taxAmount === 0 && Boolean(breakdown.countryCode && breakdown.postalCode);
}

/**
 * Производные суммы пишутся так же, как их пишет Paddle.
 *
 * Испанская локаль печатает латиноамериканские валюты кодом, а не символом:
 * `Intl('es', MXN)` даёт «54,50 MXN», тогда как сам Paddle на той же карточке
 * печатает «$109.00». Месячный эквивалент годовой цены — единственная сумма,
 * которую считаем мы, — выбивался из всей карточки.
 *
 * Локаль страны валюты печатает ровно как Paddle: `es-MX` → «$54.50», `es-AR` →
 * «$ 4.450,00», `es-CL` → «$2.995», `es-PE` → «S/ 9.95». Валюты, которых здесь
 * нет, форматируются локалью страницы — для них она и так даёт символ.
 *
 * Карта влияет только на внешний вид: цену и валюту по-прежнему выбирает Paddle
 * по стране покупателя.
 */
const CURRENCY_FORMATTING_LOCALE: Record<string, string> = {
  MXN: "es-MX",
  ARS: "es-AR",
  CLP: "es-CL",
  COP: "es-CO",
  PEN: "es-PE",
  BRL: "pt-BR",
};

export function getCurrencyFormattingLocale(currencyCode: string | null | undefined, locale: string) {
  return CURRENCY_FORMATTING_LOCALE[String(currencyCode || "").toUpperCase()] || locale;
}

/**
 * Запись цены — по правилам языка страницы, и внутри страницы она одна.
 *
 * У языков разделители противоположны: английский пишет «$5.90», испанский —
 * «5,90 US$», где запятая отделяет дробь, а приписка `US` отличает доллар от
 * местного песо. Смешать точку и запятую на одном экране могло бы только
 * четырёхзначное число («$1,234.56»), но самая большая цена у нас — $119.40 за
 * год, разряд тысяч не наступает никогда.
 *
 * Собственное форматирование нужно потому, что Paddle печатает свои строки
 * по-английски независимо от языка страницы, и рядом с нашим «4,45 €»
 * появлялось его «€8.89». Нулевые копейки не показываем — но только когда их
 * действительно ноль: слепое отбрасывание превратило бы «7,99 €» в «8 €».
 */
/**
 * Нулевые копейки определяем по тому, что Intl реально напечатает.
 *
 * Месячный эквивалент годовой цены — деление на двенадцать, и круглым оно почти
 * не бывает: 239,94 € за год Premium дают 19,995 в месяц. Сырое число не целое,
 * поэтому проверка на целость оставляла два знака, а Intl округлял их до
 * «20,00 €» — ровно тот хвост, который мы убираем.
 *
 * Считать самим нельзя: `3.995 * 100` в плавающей точке даёт 399.50000000000006
 * и округляется вверх, тогда как Intl печатает «3,99». Поэтому спрашиваем у Intl
 * готовые части числа и смотрим, из одних ли нулей состоит дробная.
 */
function formatMajorAmount(major: number, currencyCode: string, locale: string) {
  if (!Number.isFinite(major)) return null;

  const resolvedLocale = getCurrencyFormattingLocale(currencyCode, locale);
  const options: Intl.NumberFormatOptions = { style: "currency", currency: currencyCode };
  const parts = new Intl.NumberFormat(resolvedLocale, options).formatToParts(major);
  const fraction = parts.find((part) => part.type === "fraction");

  if (fraction && /^0+$/.test(fraction.value)) {
    return new Intl.NumberFormat(resolvedLocale, {
      ...options,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(major);
  }
  return parts.map((part) => part.value).join("");
}

export function formatPaddleAmount(
  minorAmount: string | null | undefined,
  currencyCode: string | null | undefined,
  locale: string,
) {
  if (minorAmount === null || minorAmount === undefined || minorAmount === "") return null;
  if (!currencyCode) return null;
  const minor = Number(minorAmount);
  if (!Number.isFinite(minor)) return null;
  const major = ZERO_DECIMAL_CURRENCIES.has(currencyCode) ? minor : minor / 100;
  return formatMajorAmount(major, currencyCode, locale);
}

export function formatPaddleMonthlyEquivalent(
  annualSubtotal: string | null,
  currencyCode: string | null,
  locale: string,
) {
  if (annualSubtotal == null || !currencyCode) return null;
  const minorAmount = Number(annualSubtotal);
  if (!Number.isFinite(minorAmount)) return null;
  const divisor = ZERO_DECIMAL_CURRENCIES.has(currencyCode) ? 1 : 100;

  return formatMajorAmount(minorAmount / divisor / 12, currencyCode, locale);
}

